import { apiListSensors } from 'apis/SensorApi';
import { apiAddCreateSensorListener, apiAddDeleteSensorListener, apiAddUpdateSensorListener, apiRemoveListener } from 'apis/SensorApi';
import { LockState } from 'models';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useAuth } from './AuthContext';
import { AccountType } from 'models';

const Context = createContext({});

export default function SensorsProvider({ children }) {
  const sensorsRef = useRef([]);
  const { currentUser } = useAuth();
  const [allSensors, setAllSensors] = useState([]); // all sensors without filtering permissions
  const [sensors, setSensors] = useState(sensorsRef.current);
  const [layers, setLayers] = useState({
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
    8: [],
    9: [],
    10: [],
    11: [],
    12: [],
    13: [],
    14: [],
    15: [],
    16: [],
    17: [],
    18: [],
    19: [],
    20: [],
    21: []
  });
  const mapContent = useRef(null);
  /***** Processing *****/
  const filterSensorsByUserPermissions = useCallback((sensors) => {
    if (!currentUser) return [];

    // If user is MASTER ROLE, return all sensors
    if (currentUser.accountType === AccountType.ADMIN) {
      return sensors;
    }

    // For other roles, filter by assigned sensor IDs
    if (currentUser?.data.nameList && Array.isArray(currentUser.data.nameList) && currentUser.data.nameList.length > 0) {
      return sensors.filter(sensor => currentUser.data.nameList.includes(sensor.id));
    }

    // If no assigned sensors, return empty array
    return [];
  }, [currentUser]);

  useEffect(() => {
    const listeners = [];
    const onCreateSensor = (sensor) => {
      // Check if user has permission to see this sensor
      const filteredSensors = filterSensorsByUserPermissions([sensor]);
      if (filteredSensors.length === 0) return; // User doesn't have permission for this sensor

      sensorsRef.current.push(sensor);
      // update layers
      mapContent.current.insert(sensor);
      const newLayers = {};
      for (let z = maxLevel; z >= minLevel; z--) {
        newLayers[z] = mapContent.current.get(z);
      }
      setLayers(newLayers);
      // update list
      sensorsRef.current.sort((s1, s2) => s1.managementId.localeCompare(s2.managementId));
      sensorsRef.current = Object.assign([], sensorsRef.current);
      setSensors(sensorsRef.current);
    }
    const onUpdateSensor = (sensor) => {
      const target = sensorsRef.current.find(s => s.id === sensor.id);
      if (!target) return; // User doesn't have this sensor

      // Check if user still has permission to see this sensor
      const filteredSensors = filterSensorsByUserPermissions([sensor]);
      if (filteredSensors.length === 0) {
        // User no longer has permission, treat as delete
        onDeleteSensor(sensor);
        return;
      }

      // update layers
      mapContent.current.delete(sensor);
      mapContent.current.insert(sensor);
      const newLayers = {};
      for (let z = maxLevel; z >= minLevel; z--) {
        newLayers[z] = mapContent.current.get(z);
      }
      setLayers(newLayers);
      // update list
      sensorsRef.current.splice(sensorsRef.current.indexOf(target), 1, sensor);
      sensorsRef.current.sort((s1, s2) => s1.managementId.localeCompare(s2.managementId))
      sensorsRef.current = Object.assign([], sensorsRef.current);
      setSensors(sensorsRef.current);
    }
    const onDeleteSensor = (sensor) => {
      // update layers
      mapContent.current.delete(sensor);
      const newLayers = {};
      for (let z = maxLevel; z >= minLevel; z--) {
        newLayers[z] = mapContent.current.get(z);
      }
      setLayers(newLayers);
      // update list
      sensorsRef.current = sensorsRef.current.filter(s => s.id !== sensor.id);
      setSensors(sensorsRef.current);
    }

    const addListeners = async () => {
      await downloadSensors();
      let res = await apiAddCreateSensorListener(onCreateSensor);
      if (res.success) listeners.push(res.data);
      res = await apiAddUpdateSensorListener(onUpdateSensor);
      if (res.success) listeners.push(res.data);
      res = await apiAddDeleteSensorListener(onDeleteSensor);
      if (res.success) listeners.push(res.data);
    }
    if (currentUser) {
      addListeners();
      downloadAllSensors();
    }
    const resetTask = setInterval(() => {
      downloadSensors();
    }, 30000);
    return () => {
      clearInterval(resetTask);
      while (true) {
        if (listeners.length === 0) break;
        apiRemoveListener(listeners.pop());
      }
    }
  }, [!!currentUser, filterSensorsByUserPermissions]);

  const downloadSensors = async () => {
    const res = await apiListSensors();
    if (res.success) {
      const filteredSensors = filterSensorsByUserPermissions(res.data.items);
      sensorsRef.current = filteredSensors;
      setSensors(sensorsRef.current);
      mapContent.current = new MapContent(filteredSensors);
      const newLayers = {};
      for (let z = maxLevel; z >= minLevel; z--) {
        newLayers[z] = mapContent.current.get(z);
      }
      setLayers(newLayers);
    }
  }

  const downloadAllSensors = async () => {
    const res = await apiListSensors();
    if (res.success) {
      setAllSensors(res.data.items);
    }
  }

  const analyze = useCallback((id) => {
    return mapContent.current.analyze(id);
  }, [])

  /*** Event Handlers ****/
  /***** Main Render *****/
  return (
    <Context.Provider value={{ sensors, layers, allSensors, analyze, reload: downloadSensors }}>
      {children}
    </Context.Provider>
  );
}

export const useSensors = () => useContext(Context);

const maxLevel = 21;
const minLevel = 3;

const DEG2RAD = Math.PI / 180;

const ConvertCoordinateToIndices = (lat, lng, zoom) => {
  let n = 2 ** Math.ceil(zoom);
  let x = Math.floor((lng + 180) / 360 * n);
  let y = Math.floor(n / 2 * (1 - Math.log(Math.tan(lat * DEG2RAD) + 1 / Math.cos(lat * DEG2RAD)) / Math.PI));
  return { x, y }
}

class MapContentItem {
  // status is a list of status values
  constructor(id, managementId, groupId, apiGroupId, lat, lng, status, isLocked = LockState.NOLOCK, name, location, description, createdAt, tx, data) {
    this.id = id;
    this.managementId = managementId;
    this.groupId = groupId;
    this.apiGroupId = apiGroupId;
    this.lat = lat;
    this.lng = lng;
    this.status = status;
    this.isLocked = isLocked;
    this.name = name;
    this.location = location;
    this.description = description;
    this.createdAt = createdAt;
    this.data = data
    if (tx) {
      this.tx = tx;
    }
    this.count = 1;
  }

  combine(item, zoomLevel, xIndex, yIndex) {
    this.id = item.groupId + "#" + zoomLevel + "#" + xIndex + "#" + yIndex + "#" + item.data;
    delete this.managementId;
    delete this.apiGroupId;
    delete this.name;
    delete this.location;
    delete this.description;
    delete this.createdAt;
    delete this.tx;

    if (this.count === 1) {
      this.status = [this.status];
      this.groupId = [this.groupId];
      this.isLocked = [this.isLocked];
      this.data = [this.data];
    }
    if (item.count === 1) {
      this.status.push(item.status);
      this.groupId.push(item.groupId);
      this.isLocked.push(item.isLocked);
      this.data.push(item.data);
    }
    else {
      this.status.push(...item.status);
      this.groupId.push(...item.groupId);
      this.isLocked.push(...item.isLocked);
      this.data.push(...item.data);
    }
    this.lat = (this.lat * this.count + item.lat * item.count) / (this.count + item.count);
    this.lng = (this.lng * this.count + item.lng * item.count) / (this.count + item.count);
    this.count = this.count + item.count;
  }

  /**
   * make a copy of item without content
   */
  replicate() {
    const replica = new MapContentItem(
      this.id,
      this.managementId,
      this.groupId,
      this.apiGroupId,
      this.lat,
      this.lng,
      this.status,
      this.isLocked,
      this.name,
      this.location,
      this.description,
      this.createdAt,
      this.tx,
      this.data
    )
    replica.count = this.count;
    return replica;
  }
}

class MapContent {
  constructor(sensors) {
    this.content_tree = {};
    this.content_map = {};
    for (const sensor of sensors) {
      this.insert(sensor);
    }
  }

  insert(sensor) {
    // init tree, dict and map
    if (!this.content_tree) {
      this.content_tree = {};
      this.content_map = {};
    }
    // init MapContentItem 
    const item = new MapContentItem(
      sensor.id,
      sensor.managementId,
      sensor.groupId,
      sensor.apiGroupId,
      sensor.lat,
      sensor.lng,
      sensor.status,
      sensor.isLocked,
      sensor.name,
      sensor.location,
      sensor.description,
      sensor.createdAt,
      sensor.tx,
      sensor.data?.typeCamera || "SINGLE"
    );
    // find location of map tile
    let { x, y } = ConvertCoordinateToIndices(sensor.lat, sensor.lng, maxLevel);
    let mapZoom = null;
    let mapCol = null;
    // insert to map
    if (!this.content_map[x]) {
      this.content_map[x] = {};
    }
    if (!this.content_map[x][y]) {
      this.content_map[x][y] = {};
    }
    this.content_map[x][y][sensor.id] = item;
    // insert to tree
    for (let zoom = maxLevel; zoom >= minLevel; zoom = zoom - 1) {
      if (!this.content_tree[zoom]) {
        this.content_tree[zoom] = {};
      }
      mapZoom = this.content_tree[zoom];
      if (!mapZoom[x]) {
        mapZoom[x] = {};
      }
      mapCol = mapZoom[x]
      if (!mapCol[y]) {
        mapCol[y] = [item.replicate()];
      }
      else {
        const index = mapCol[y].findIndex(col => col.groupId.includes(item.groupId) && col.data.includes(item.data));
        if (index >= 0) {
          mapCol[y][index].combine(item, zoom, x, y);
        } else {
          mapCol[y].push(item.replicate())
        }
      }
      x = x >> 1;
      y = y >> 1;
    }
  }
  /**
   * マップコンテンツに削除
   * @param {MapContentItem} item 
   */
  delete(sensor) {
    let { x, y } = ConvertCoordinateToIndices(sensor.lat, sensor.lng, maxLevel);
    let items = null;
    // delete in map
    if (this.content_map[x] && this.content_map[x][y]) {
      delete this.content_map[x][y][sensor.id];
      // if there is no item in this tile, remove it
      items = Object.values(this.content_map[x][y]);
      if (items.length === 0) {
        delete this.content_map[x][y];
      }
    }
    // delete in tree by first updating the node at maxlevel
    let mapTile = [];
    let mapLevel = this.content_tree[maxLevel];
    if (mapLevel && mapLevel[x] && mapLevel[x][y]) {
      if (items.length === 0) {
        delete mapLevel[x][y];
      }
      else {
        mapLevel[x][y] = [items[0].replicate()];
        mapTile = mapLevel[x][y];
        for (let i = 1; i < items.length; i++) {
          const item = items[i];
          const index = mapTile.findIndex(col => col.groupId.includes(item.groupId) && col.data.includes(item.data));
          if (index >= 0) {
            mapTile[index].combine(item, maxLevel, x, y);
          } else {
            mapTile.push(item.replicate())
          }
        }
      }
      x = x >> 1;
      y = y >> 1;
    }
    else {
      return;
    }
    // update other nodes at lower levels
    let preMapLevel = null;
    let px = 0, py = 0;
    for (let zoom = maxLevel - 1; zoom >= minLevel; zoom--) {
      mapLevel = this.content_tree[zoom];
      preMapLevel = this.content_tree[zoom + 1];
      px = x << 1;
      py = y << 1;
      // reset mapTile
      mapTile = [];
      if (preMapLevel) {
        if (preMapLevel[px]) {
          if (preMapLevel[px][py]) {
            mapTile = preMapLevel[px][py]
          }
          if (preMapLevel[px][py + 1]) {
            const items = preMapLevel[px][py + 1]
            for (let index = 0; index < items.length; index++) {
              const element = items[index];
              const existIdx = mapTile.findIndex(col => col.groupId.includes(element.groupId) && col.data.includes(element.data))
              if (existIdx >= 0) {
                mapTile[existIdx].combine(element, zoom, x, y);
              } else {
                mapTile.push(element.replicate())
              }
            }
          }
        }
        if (preMapLevel[px + 1]) {
          if (preMapLevel[px + 1][py]) {
            const items = preMapLevel[px + 1][py]
            for (let index = 0; index < items.length; index++) {
              const element = items[index];
              const existIdx = mapTile.findIndex(col => col.groupId.includes(element.groupId) && col.data.includes(element.data))
              if (existIdx >= 0) {
                mapTile[existIdx].combine(element, zoom, x, y);
              } else {
                mapTile.push(element.replicate())
              }
            }
          }
          if (preMapLevel[px + 1][py + 1]) {
            const items = preMapLevel[px + 1][py + 1]
            for (let index = 0; index < items.length; index++) {
              const element = items[index];
              const existIdx = mapTile.findIndex(col => col.groupId.includes(element.groupId) && col.data.includes(element.data))
              if (existIdx >= 0) {
                mapTile[existIdx].combine(element, zoom, x, y);
              } else {
                mapTile.push(element.replicate())
              }
            }
          }
        }
      }
      if (mapTile.length > 0) {
        if (!mapLevel[x]) mapLevel[x] = {};
        if (!mapLevel[x][y]) mapLevel[x][y] = {};
        mapLevel[x][y] = mapTile;
      }
      else {
        if (mapLevel[x] && mapLevel[x][y]) delete mapLevel[x][y];
        if (Object.keys(mapLevel[x]).length == 0) delete mapLevel[x];
      }
      x = x >> 1;
      y = y >> 1;
    }
  }
  /**
   * get all sensors from a layer
   * @param {number} layer 
   */
  get(layer) {
    const sensors = [];
    for (const x of Object.keys(this.content_tree[layer])) {
      for (const y of Object.keys(this.content_tree[layer][x])) {
        sensors.push(...this.content_tree[layer][x][y]);
      }
    }
    return sensors;
  }
  /**
   * 
   * @param {string} id group id
   * @returns return a list of sensor ids
   */
  analyze(id) {
    let tx = 0, ty = 0;
    let pos = id.split("#");
    const groupId = pos[0];
    const zoom = parseInt(pos[1]);
    const cx = parseInt(pos[2]);
    const cy = parseInt(pos[3]);
    const data = pos[4];
    const d_zoom = maxLevel - zoom;
    const items = [];
    const sortedXs = Object.keys(this.content_map).map(key => parseInt(key)).sort();
    for (const x of sortedXs) {
      const column = this.content_map[x];
      tx = x >> d_zoom;
      if (tx !== cx) continue;
      for (const y of Object.keys(column)) {
        const mapTile = column[y];
        ty = y >> d_zoom;
        if (ty !== cy) continue;
        for (const item of Object.values(mapTile)) {
          if (item.groupId === groupId && item.data === data) {
            items.push(item.id);
          }
        }
      }
    }
    return items;
  }
}